import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    Switch,
    Chip,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Grid,
    MenuItem,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    LocationCity as CityIcon,
    Hub as RegionIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import locationService from '../../services/locationService';
import ConfirmDialog from '../ConfirmDialog';

function CityManagement({ onDataChange }) {
    const [cities, setCities] = useState([]);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });

    const [countryFilter, setCountryFilter] = useState('all');
    const [stateFilter, setStateFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCity, setEditingCity] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [formData, setFormData] = useState({
        country_id: '',
        state_id: '',
        name: '',
        city_short: '',
        status: 'Y'
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, city: null });
    const { handleApiCallWithLoading, showError, showSuccess } = useApiHandler();

    useEffect(() => {
        loadCities();
        loadCountries();
        loadRegions();
    }, []);

    useEffect(() => {
        if (countryFilter !== 'all') {
            loadStates(countryFilter);
        } else {
            setStates([]);
            setStateFilter('all');
        }
    }, [countryFilter]);

    useEffect(() => {
        setPage(0);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    }, [searchTerm, countryFilter, stateFilter, regionFilter, statusFilter]);

    useEffect(() => {
        loadCities();
    }, [page, rowsPerPage]);

    const loadCities = async () => {
        try {
            setLoading(true);
            const params = {
                page: page + 1, // Convert 0-based to 1-based
                per_page: rowsPerPage
            };
            
            if (searchTerm) params.search = searchTerm;
            if (regionFilter !== 'all') params.region_id = regionFilter;
            if (stateFilter !== 'all') params.state_id = stateFilter;
            if (statusFilter !== 'all') params.status = statusFilter;

            const response = await locationService.getCities(params);
            if (response.success) {
                setCities(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (error) {
            showError('Error loading cities');
        } finally {
            setLoading(false);
        }
    };

    const loadCountries = async () => {
        try {
            const response = await locationService.getCountriesDropdown();
            if (response.success) {
                setCountries(response.data);
            }
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    };

    const loadStates = async (countryId) => {
        try {
            const response = await locationService.getStatesDropdown(countryId);
            if (response.success) {
                setStates(response.data);
            }
        } catch (error) {
            console.error('Error loading states:', error);
        }
    };

    const loadRegions = async () => {
        try {
            const response = await locationService.getRegionsDropdown();
            if (response.success) {
                setRegions(response.data);
            }
        } catch (error) {
            console.error('Error loading regions:', error);
        }
    };

    const handleSearch = () => {
        loadCities();
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setCountryFilter('all');
        setStateFilter('all');
        setRegionFilter('all');
        setStatusFilter('all');
        loadCities();
    };

    const handleOpenDialog = (city = null) => {
        if (city) {
            setEditingCity(city);
            setFormData({
                country_id: city.state?.country_id || city.country?.country_id || '',
                state_id: city.state?.state_id || '',
                name: city.name || '',
                city_short: city.city_short || '',
                status: city.status || 'Y'
            });
            if (city.state?.country_id || city.country?.country_id) {
                loadStates(city.state?.country_id || city.country?.country_id);
            }
        } else {
            setEditingCity(null);
            setFormData({
                country_id: '',
                state_id: '',
                name: '',
                city_short: '',
                status: 'Y'
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCity(null);
        setFormData({
            country_id: '',
            state_id: '',
            name: '',
            city_short: '',
            status: 'Y'
        });
        setFormErrors({});
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'country_id') {
            setFormData(prev => ({
                ...prev,
                state_id: ''
            }));
            if (value) {
                loadStates(value);
            } else {
                setStates([]);
            }
        }

        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'City name is required';
        }
        
        if (!formData.country_id) {
            errors.country_id = 'Country is required';
        }
        
        if (!formData.state_id) {
            errors.state_id = 'State is required';
        }
        
        if (!formData.status) {
            errors.status = 'Status is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const cityData = {
                name: formData.name.trim(),
                city_short: formData.city_short.trim() || null,
                state_id: parseInt(formData.state_id),
                country_id: parseInt(formData.country_id),
                status: formData.status
            };

            console.log('Sending city data:', cityData);
            console.log('Form data status:', formData.status);

            let response;
            if (editingCity) {
                response = await locationService.updateCity(editingCity.id, cityData);
            } else {
                response = await locationService.createCity(cityData);
            }

            if (response.success) {
                            showSuccess(editingCity ? 'City updated successfully' : 'City created successfully');
                handleCloseDialog();
                loadCities();
                if (onDataChange) onDataChange();
            }
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'An error occurred';
            showError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await locationService.deleteCity(deleteDialog.city.id);
            if (response.success) {
                showSuccess('City deleted successfully');
                loadCities();
                if (onDataChange) onDataChange();
            }
            setDeleteDialog({ open: false, city: null });
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Error deleting city';
            showError(message);
            setDeleteDialog({ open: false, city: null });
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search cities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 250 }}
                />
                
                <TextField
                    select
                    size="small"
                    label="Country"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="all">All Countries</MenuItem>
                    {countries.map((country) => (
                        <MenuItem key={country.country_id} value={country.country_id.toString()}>
                            {country.name}
                        </MenuItem>
                    ))}
                </TextField>
                
                <TextField
                    select
                    size="small"
                    label="State"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    disabled={countryFilter === 'all'}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="all">All States</MenuItem>
                    {states.map((state) => (
                        <MenuItem key={state.state_id} value={state.state_id.toString()}>
                            {state.name}
                        </MenuItem>
                    ))}
                </TextField>
                
                <TextField
                    select
                    size="small"
                    label="Region"
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="all">All Regions</MenuItem>
                    {regions.map((region) => (
                        <MenuItem key={region.id} value={region.id.toString()}>
                            {region.name}
                        </MenuItem>
                    ))}
                </TextField>
                
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 120 }}
                >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Y">Active</MenuItem>
                    <MenuItem value="N">Inactive</MenuItem>
                </TextField>
                


                <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                >
                    Search
                </Button>

                <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add City
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Short Name</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Region</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow key="loading">
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : cities.length === 0 ? (
                            <TableRow key="no-data">
                                <TableCell colSpan={7} align="center">
                                    No cities found
                                </TableCell>
                            </TableRow>
                        ) : (
                            cities.map((city) => (
                                <TableRow key={city.id}>
                                    <TableCell>{city.name}</TableCell>
                                    <TableCell>{city.city_short || '-'}</TableCell>
                                    <TableCell>{city.state?.name || '-'}</TableCell>
                                    <TableCell>{city.country?.name || city.state?.country?.name || '-'}</TableCell>
                                    <TableCell>
                                        {city.region ? city.region.name : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={city.status === 'Y' ? 'Active' : 'Inactive'} 
                                            color={city.status === 'Y' ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(city)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteDialog({ open: true, city: city })}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => 
                        `${pagination.from || from}-${pagination.to || to} of ${pagination.total || count}`
                    }
                />
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCity ? 'Edit City' : 'Add New City'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Country"
                                value={formData.country_id}
                                onChange={(e) => handleInputChange('country_id', e.target.value)}
                                error={!!formErrors.country_id}
                                helperText={formErrors.country_id}
                                required
                            >
                                <MenuItem value="">Select Country</MenuItem>
                                {countries.map((country) => (
                                    <MenuItem key={country.country_id} value={country.country_id}>
                                        {country.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="State"
                                value={formData.state_id}
                                onChange={(e) => handleInputChange('state_id', e.target.value)}
                                error={!!formErrors.state_id}
                                helperText={formErrors.state_id}
                                disabled={!formData.country_id}
                                required
                            >
                                <MenuItem value="">Select State</MenuItem>
                                {states.map((state) => (
                                    <MenuItem key={state.state_id} value={state.state_id}>
                                        {state.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="City Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="City Short Name"
                                value={formData.city_short}
                                onChange={(e) => handleInputChange('city_short', e.target.value)}
                                error={!!formErrors.city_short}
                                helperText={formErrors.city_short || "Short name for the city"}
                                required
                            />
                        </Grid>
                        {editingCity && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Current Region"
                                    value={editingCity.region?.name || 'Not assigned to any region'}
                                    disabled
                                    helperText="Region assignment is managed from the Region Management page"
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                value={formData.status}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                error={!!formErrors.status}
                                helperText={formErrors.status}
                                required
                            >
                                <MenuItem value="Y">Active</MenuItem>
                                <MenuItem value="N">Inactive</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={20} /> : (editingCity ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                title="Delete City"
                content={`Are you sure you want to delete "${deleteDialog.city?.name}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                onClose={() => setDeleteDialog({ open: false, city: null })}
            />
        </Box>
    );
}

export default CityManagement; 